import { useTranslation } from 'react-i18next';
import { Outlet, useMatches } from 'react-router';

import { cn } from '@/shared/ui/cn';

import { BottomTabs } from '@/app/shell/BottomTabs';
import { ShortcutsDialog } from '@/app/shell/ShortcutsDialog';
import { Sidebar } from '@/app/shell/Sidebar';
import type { RouteLayout } from '@/app/route-manifest';

type Handle = { layout?: RouteLayout };

/** L'habillage demandé par la route la plus profonde. */
export function useRouteLayout(): RouteLayout {
  const matches = useMatches();
  for (let index = matches.length - 1; index >= 0; index -= 1) {
    const handle = matches[index].handle as Handle | undefined;
    if (handle?.layout) {
      return handle.layout;
    }
  }
  return 'stack';
}

/**
 * L'ossature de l'application connectée.
 *
 * - Ordinateur : la barre latérale, toujours là ; le contenu à sa droite.
 * - Téléphone : une racine d'onglet (`tab`) porte la barre d'onglets en bas ; une
 *   page poussée (`stack`) dessine son en-tête avec retour, sans onglets, comme
 *   une page empilée du mobile ; la scène (`immersive`) prend tout l'écran.
 */
export function AppShell() {
  const { t } = useTranslation(['common', 'web', 'account']);
  const layout = useRouteLayout();
  const immersive = layout === 'immersive';

  return (
    <div className="min-h-dvh bg-bg">
      <a
        href="#main"
        className="sr-only z-50 rounded-sm bg-fg px-3 py-2 text-subtle text-bg focus:not-sr-only focus:fixed focus:top-3 focus:left-3"
      >
        {t('web:shell.skipToContent')}
      </a>
      <Sidebar />
      <main
        id="main"
        className={cn(
          'min-h-dvh md:pl-[72px] xl:pl-[244px]',
          immersive
            ? 'bg-stage'
            : layout === 'tab' && 'pb-[calc(64px+env(safe-area-inset-bottom,0px))] md:pb-0',
          // La barre d'onglets du téléphone : une barre d'action se pose au-dessus d'elle.
          layout === 'tab' &&
            '[--action-bar-safe:0px] [--bottom-bar:calc(56px+env(safe-area-inset-bottom,0px))] [--page-fill:calc(100dvh-64px-env(safe-area-inset-bottom,0px))] md:[--bottom-bar:0px]',
        )}
        {...(immersive ? { 'data-theme': 'dark' } : {})}
      >
        <Outlet />
      </main>
      {layout === 'tab' ? <BottomTabs /> : null}
      <ShortcutsDialog />
    </div>
  );
}
